// Sprint 12 (D-008) — chat-header status pill.
//
// Sprint S20.5 PR-4 (D013-EC-70) extends the pill to render the idle
// hint when status === 'waiting'. Before this sprint the pill rendered
// ONLY for `running` (D-008 binary states); founder reported the silent
// idle state as "stuck", so we surface a subtle "Idle · Send a message
// to continue." cue. The two pills are visually distinct (running is
// success-green, idle is muted) so the founder reads them at a glance.
// All other statuses (finished / interrupted / stuck / error) still
// return null — the chat header does not surface terminal states.

import type { AgentStatus } from '../../types';
import { useTranslation } from '../../i18n';

export interface StatusPillProps {
  status: AgentStatus;
}

export function StatusPill({ status }: StatusPillProps) {
  // Rules-of-Hooks: useTranslation must run before any conditional return.
  // Detected by react-hooks/rules-of-hooks lint added in S11b.2 hotfix —
  // the original code put the `status !== 'running'` early return above
  // the hook, which crashes with "rendered fewer hooks than expected" on
  // the running → idle transition the moment React unmounts the pill.
  const { t } = useTranslation();

  if (status === 'running') {
    return (
      <span
        className={[
          'inline-flex items-center shrink-0 px-pad-chip-x py-pad-chip-y',
          'rounded-full text-caption font-550 tabular-nums',
          'bg-status-success/15 text-status-success',
        ].join(' ')}
        data-testid="chat-header-status-pill"
        data-status="running"
      >
        {t('status.running')}
      </span>
    );
  }

  if (status === 'waiting') {
    return (
      <span
        className={[
          'inline-flex items-center shrink-0 px-pad-chip-x py-pad-chip-y',
          'rounded-full text-caption font-450 tabular-nums',
          'bg-muted/60 text-muted-foreground',
        ].join(' ')}
        data-testid="chat-header-status-pill"
        data-status="waiting"
      >
        {t('chat.idleHint')}
      </span>
    );
  }

  return null;
}
